/* eslint-disable no-undef, no-unused-vars */
import * as React from "react";
import { observer } from "mobx-react-lite";
import {
  App,
  Button,
  DatePicker,
  Drawer,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Table,
  TableColumnsType,
  TableProps,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useStore } from "../../root-store-context";
import { Job } from "../../stores/AppStore/ApplicationStore";
import "./style.scss";

const { Text } = Typography;

type OnChange = NonNullable<TableProps<Job>["onChange"]>;
type Filters = Parameters<OnChange>[1];
type GetSingle<T> = T extends (infer U)[] ? U : never;
type Sorts = GetSingle<Parameters<OnChange>[2]>;
type CardSortKey = "none" | "JobID" | "Property" | "CommunicationType" | "DateCreated" | "ProcessedBy";
type CardSortDir = "asc" | "desc";
const CARD_SORT_OPTIONS: { value: CardSortKey; label: string }[] = [
  { value: "none", label: "None" },
  { value: "JobID", label: "Mailing ID" },
  { value: "Property", label: "Property" },
  { value: "CommunicationType", label: "Communication Type" },
  { value: "DateCreated", label: "Date Created" },
  { value: "ProcessedBy", label: "Processed By" },
];
const DEFAULT_SORT: Sorts = { columnKey: "JobID", order: "descend" };

const CommunicationType = observer(() => {
  const {
    applicationStore,
    modalViewerStore,
  } = useStore();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const { message, modal, notification } = App.useApp();
  const [rejectForm] = Form.useForm();

  const [filteredInfo, setFilteredInfo] = useState<Filters>({});
  const [sortedInfo, setSortedInfo] = useState<Sorts>(DEFAULT_SORT);
  const [cardSearch, setCardSearch] = useState<string>("");
  const [cardPage, setCardPage] = useState<number>(1);
  const CARD_PAGE_SIZE = 20;
  const [cardSortBy, setCardSortBy] = useState<CardSortKey>("JobID");
  const [cardSortDir, setCardSortDir] = useState<CardSortDir>("desc");

  const [filterStatusId, setFilterStatusId] = useState<number | undefined>(undefined);
  const [filterDateRange, setFilterDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const APPROVED_STATUS_ID = 1;
  const datePickerSelecting = useRef<Dayjs | null>(null);

  const handleChange: OnChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter as Sorts);
  };

  const filteredJobs = jobs.filter((job) => {
    if (!cardSearch) return true;
    const q = cardSearch.toLowerCase();
    return (
      job.JobID.toString().includes(q) ||
      (job.CommunicationType || "").toLowerCase().includes(q) ||
      (job.Property || "").toLowerCase().includes(q) ||
      (job.ProcessedBy || "").toLowerCase().includes(q) ||
      (job.DateCreated || "").toLowerCase().includes(q) ||
      (job.Status || "").toLowerCase().includes(q)
    );
  });

  const sortedJobs = [...filteredJobs].sort((a, b) => {
    if (cardSortBy === "none") return 0;

    if (cardSortBy === "DateCreated") {
      const dateA = new Date(a.DateCreated || "").getTime();
      const dateB = new Date(b.DateCreated || "").getTime();
      return cardSortDir === "asc" ? dateA - dateB : dateB - dateA;
    }

    if (cardSortBy === "JobID") {
      return cardSortDir === "asc" ? a.JobID - b.JobID : b.JobID - a.JobID;
    }
    const valA = (a[cardSortBy as keyof Job] ?? "").toString().toLowerCase();
    const valB = (b[cardSortBy as keyof Job] ?? "").toString().toLowerCase();
    const cmp = valA.localeCompare(valB);
    return cardSortDir === "asc" ? cmp : -cmp;
  });

  const paginatedJobs = sortedJobs.slice(0, cardPage * CARD_PAGE_SIZE);

  useEffect(() => {
    const calculateHeight = () => setWindowHeight(window.innerHeight);
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  const loadJobList = async (statusId?: number, startDate?: string, endDate?: string) => {
    setJobs([]);
    setIsLoading(true);
    const res = await applicationStore.getJobListAsync(statusId, startDate, endDate);
    setJobs([...res].sort((a, b) => b.JobID - a.JobID));
    setIsOpenModal(true);
    setIsLoading(false);
  };

  const loadJobListOnClick = () => {
    setJobs([]);
    setFilteredInfo({});
    setSortedInfo(DEFAULT_SORT);
    setCardSearch("");
    setCardPage(1);
    setCardSortBy("JobID");
    setCardSortDir("desc");
    setFilterStatusId(undefined);
    setFilterDateRange(null);
    setHasSearched(false);
    setIsOpenModal(true);
  };

  const handleSearch = async () => {
    if (filterStatusId === undefined) {
      message.error("Please select a status.");
      return;
    }
    if (filterStatusId === APPROVED_STATUS_ID && !filterDateRange) {
      message.error("Please select a date range.");
      return;
    }
    const startDate = filterStatusId === APPROVED_STATUS_ID ? filterDateRange![0].format("YYYY-MM-DD") : undefined;
    const endDate = filterStatusId === APPROVED_STATUS_ID ? filterDateRange![1].format("YYYY-MM-DD") : undefined;
    setFilteredInfo({});
    setSortedInfo(DEFAULT_SORT);
    setCardSearch("");
    setCardPage(1);
    setCardSortBy("JobID");
    setCardSortDir("desc");
    setHasSearched(true);
    await loadJobList(filterStatusId, startDate, endDate);
  };

  const checkOpenMailJob = async (): Promise<void> => {
    if (
      applicationStore.isOpenJobList &&
      applicationStore.isJobListItemID !== ""
    ) {
      applicationStore.setIsOpenJobList(false);
      await loadJobList();
      setFilteredInfo({ JobID: [applicationStore.isJobListItemID] });
      setSortedInfo(DEFAULT_SORT);
    }
  };

  useEffect(() => {
    if (applicationStore.accessEmployee.employeeKey) {
      checkOpenMailJob();
    }
  }, [applicationStore.accessEmployee.employeeKey]);

  const onPreview = (mailJobID: number) => {
    applicationStore
      .GetDocumentPreviewFile(mailJobID)
      .then((res) => {
        modalViewerStore.openFileByUrl(
          res.inputFileBase64,
          `MailJob ${mailJobID}.pdf`
        );
        setIsModalLoading(false);
      })
      .catch((error) => {
        message.error(error);
        setIsModalLoading(false);
      });
  };

  const RowButtons = (props: { record: Job }) => {
    const [record, setRecord] = useState<Job>(props.record);
    const [isAproveRejectLoading, setIsAproveRejectLoading] =
      useState<boolean>(false);

    useEffect(() => {
      setRecord(props.record);
    }, [props.record]);

    const onReject = (job: Job) => {
      rejectForm.resetFields();
      return new Promise<void>((resolve, reject) => {
        modal.confirm({
          className: "cam__confirm-modal",
          title: "Reject Reason",
          content: (
            <>
              <Text>To reject the job, enter the reason for the rejection.</Text>
              <Form form={rejectForm}>
                <Form.Item name="rejectReason">
                  <Input placeholder="Reason" name="rejectReason" />
                </Form.Item>
              </Form>
            </>
          ),
          okText: "Reject",
          cancelText: "Cancel",
          onOk: async () => {
            const result = await applicationStore.Reject(
              job.JobID,
              rejectForm.getFieldValue("rejectReason")
            );
            if (result) {
              notification.success({
                message: `Mailing ID ${job.JobID} has been rejected.`,
                duration: 10,
              });
              setJobs((prev) =>
                prev.map((i) =>
                  i.JobID === job.JobID
                    ? { ...i, Status: "Rejected", isCanApprove: false }
                    : i
                )
              );
              resolve();
            } else {
              notification.error({
                message:
                  "An error occurred while rejecting this mailing. Please refresh the page and try again.",
                duration: 10,
              });
              reject();
            }
          },
          onCancel: () => reject(),
        });
      });
    };

    const onApprove = (job: Job) => {
      setIsAproveRejectLoading(true);
      applicationStore.Approve(job.JobID).then((res) => {
        if (res) {
          notification.success({
            message: `Mailing ID ${job.JobID} has been approved.`,
            duration: 10,
          });
          setJobs((prev) =>
            prev.map((i) =>
              i.JobID === job.JobID
                ? { ...i, Status: "Sent to Nordis", isCanApprove: false }
                : i
            )
          );
        } else {
          notification.error({
            message:
              "An error occurred while approving this mailing. Please refresh the page and try again.",
            duration: 10,
          });
        }
        setIsAproveRejectLoading(false);
      });
    };

    const onReviewRecipientInfo = (job: Job) => {
      applicationStore.GetMailJobRecipientExelFile(job.JobID).then((res) => {
        const url = window.URL.createObjectURL(new Blob([res]));
        modalViewerStore.openFileByUrl(
          url,
          `MailingJobRecipientsInfo ${job.JobID}.xlsx`
        );
        setRecord((prev) => ({ ...prev, isReviewed: true }));
      });
    };

    return (
      <div>
        <div style={{ width: "100px", left: "52.5%", position: "absolute" }}>
          {record.CommunicationTypeId === 1 && (
            <Button data-testid={`cam-preview-btn-${record.JobID}`} onClick={() => onPreview(record.JobID)}>Preview</Button>
          )}
        </div>
        {record.StatusId !== 5 && (
          <div style={{ display: "grid", width: "37%", float: "right" }}>
            <div style={{ gridRow: "1/3" }}>
              <Button data-testid={`cam-review-recipient-btn-${record.JobID}`} onClick={() => onReviewRecipientInfo(record)}>
                Review Recipient Info
              </Button>
            </div>
            {record.isCanApprove && (
              <>
                <div style={{ gridRow: "1/3" }}>
                  <Button
                    data-testid={`cam-approve-btn-${record.JobID}`}
                    loading={isAproveRejectLoading}
                    disabled={!record.isReviewed}
                    onClick={() => onApprove(record)}
                  >
                    Approve
                  </Button>
                </div>
                <div style={{ gridRow: "1/3", textAlign: "center" }}>
                  <Button
                    data-testid={`cam-reject-btn-${record.JobID}`}
                    loading={isAproveRejectLoading}
                    disabled={!record.isReviewed}
                    onClick={() => onReject(record)}
                  >
                    Reject
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const jobMatchesColumnFilter = (job: Job, columnKey: string, filterValues: string[]): boolean => {
    const fieldValue = job[columnKey as keyof Job]?.toString() ?? "";
    return filterValues.some((filterValue) => fieldValue === filterValue);
  };

  const DATE_COLUMNS = new Set(["DateCreated", "DateApproved"]);

  const filterByProp = (columnKey: string): { text: string; value: string }[] => {
    const jobsFilteredByOtherColumns = jobs.filter((job) =>
      Object.entries(filteredInfo).every(([activeColumnKey, activeFilterValues]) => {
        if (activeColumnKey === columnKey || !activeFilterValues || activeFilterValues.length === 0) return true;
        return jobMatchesColumnFilter(job, activeColumnKey, activeFilterValues as string[]);
      })
    );

    const uniqueValues = [
      ...new Set(
        jobsFilteredByOtherColumns
          .map((job) => job[columnKey as keyof Job]?.toString() ?? "")
          .filter(Boolean)
      ),
    ];

    if (DATE_COLUMNS.has(columnKey)) {
      return uniqueValues
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
        .map((value) => ({ text: value, value }));
    }
    return uniqueValues
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ text: value, value }));
  };

  const columns: TableColumnsType<Job> = [
    {
      title: "Job ID",
      dataIndex: "JobID",
      key: "JobID",
      sorter: (a, b) => a.JobID - b.JobID,
      sortOrder: sortedInfo.columnKey === "JobID" ? sortedInfo.order : null,
      filters: filterByProp("JobID"),
      filteredValue: filteredInfo.JobID || null,
      onFilter: (value, record) => jobMatchesColumnFilter(record, "JobID", [value as string]),
    },
    {
      title: "Communication Type",
      dataIndex: "CommunicationType",
      key: "CommunicationType",
      width: "15%",
      sorter: (a, b) =>
        (a.CommunicationType || "").localeCompare(b.CommunicationType || ""),
      sortOrder: sortedInfo.columnKey === "CommunicationType" ? sortedInfo.order : null,
      filters: filterByProp("CommunicationType"),
      filteredValue: filteredInfo.CommunicationType || null,
      onFilter: (value, record) => jobMatchesColumnFilter(record, "CommunicationType", [value as string]),
    },
    {
      title: "Property",
      dataIndex: "Property",
      key: "Property",
      width: "12%",
      sorter: (a, b) => (a.Property || "").localeCompare(b.Property || ""),
      sortOrder: sortedInfo.columnKey === "Property" ? sortedInfo.order : null,
      filters: filterByProp("Property"),
      filteredValue: filteredInfo.Property || null,
      onFilter: (value, record) => jobMatchesColumnFilter(record, "Property", [value as string]),
    },
    {
      title: "Processed By",
      dataIndex: "ProcessedBy",
      key: "ProcessedBy",
      width: "11%",
      sorter: (a, b) =>
        (a.ProcessedBy || "").localeCompare(b.ProcessedBy || ""),
      sortOrder: sortedInfo.columnKey === "ProcessedBy" ? sortedInfo.order : null,
      filters: filterByProp("ProcessedBy"),
      filteredValue: filteredInfo.ProcessedBy || null,
      onFilter: (value, record) => jobMatchesColumnFilter(record, "ProcessedBy", [value as string]),
    },
    {
      title: "Date Created",
      dataIndex: "DateCreated",
      key: "DateCreated",
      width: "10%",
      sorter: (a, b) => new Date(a.DateCreated || "").getTime() - new Date(b.DateCreated || "").getTime(),
      sortOrder: sortedInfo.columnKey === "DateCreated" ? sortedInfo.order : null,
      filters: filterByProp("DateCreated"),
      filteredValue: filteredInfo.DateCreated || null,
      onFilter: (value, record) => jobMatchesColumnFilter(record, "DateCreated", [value as string]),
    },
    {
      title: "Recipient(s)",
      dataIndex: "Recipients",
      key: "Recipients",
      width: "12%",
      sorter: (a, b) =>
        a.Recipients.toString().localeCompare(b.Recipients.toString()),
      sortOrder: sortedInfo.columnKey === "Recipients" ? sortedInfo.order : null,
      filters: filterByProp("Recipients"),
      filteredValue: filteredInfo.Recipients || null,
      onFilter: (value, record) => jobMatchesColumnFilter(record, "Recipients", [value as string]),
    },
    {
      title: "Date Approved",
      dataIndex: "DateApproved",
      key: "DateApproved",
      width: "15%",
      filters: filterByProp("DateApproved"),
      filteredValue: filteredInfo.DateApproved || null,
      onFilter: (value, record) => jobMatchesColumnFilter(record, "DateApproved", [value as string]),
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: "15%",
    },
  ];

  return (
    <>
      <div className="cam__select-joblist-row">
        <Select
          data-testid="cam-comm-type-select"
          style={{ width: "300px", maxWidth: "100%", zIndex: 1 }}
          placeholder="Select a communication type..."
          onChange={(item) => {
            const types = Array.isArray(applicationStore.communicationTypes)
              ? applicationStore.communicationTypes
              : [];
            const communication = types.find((_) => _.comTypeId === item);
            if (communication) {
              applicationStore.setCommunicationType(communication);
            }
          }}
          filterSort={(optionA, optionB) =>
            (optionA?.label ?? "")
              .toLowerCase()
              .localeCompare((optionB?.label ?? "").toLowerCase())
          }
          options={(
            Array.isArray(applicationStore.communicationTypes)
              ? applicationStore.communicationTypes
              : []
          ).map((type) => ({
            value: type.comTypeId,
            label: type.comTypeName,
          }))}
        />
        <Button
          data-testid="cam-job-list-btn"
          loading={isLoading}
          onClick={loadJobListOnClick}
          className="cam__joblist-btn"
        >
          Job List
        </Button>
      </div>

      <Drawer
        title="Job List"
        placement="bottom"
        height={windowHeight}
        closable={false}
        extra={
          <Space>
            <Button data-testid="cam-drawer-cancel-btn" onClick={() => { if (!isModalLoading) setIsOpenModal(false); }}>
              Cancel
            </Button>
          </Space>
        }
        open={isOpenModal}
      >
        <Spin spinning={isModalLoading}>

          {/* ── Filters ── */}
          <div className="cam__joblist-filters">
            <div className="cam__joblist-filter-field">
              <Text style={{ fontSize: 12, fontWeight: 600 }}>Status</Text>
              <Select
                data-testid="cam-status-filter-select"
                placeholder="Select a status"
                style={{ width: "100%" }}
                value={filterStatusId}
                options={applicationStore.statuses.map((s) => ({
                  value: s.statusId,
                  label: s.statusName,
                }))}
                onChange={(val) => {
                  setFilterStatusId(val);
                  setFilterDateRange(null);
                }}
              />
            </div>
            {filterStatusId === APPROVED_STATUS_ID && (
              <div className="cam__joblist-filter-field">
                <Text style={{ fontSize: 12, fontWeight: 600 }}>Date</Text>
                <DatePicker.RangePicker
                  data-testid="cam-date-range-picker"
                  format={{ format: "MM/DD/YYYY", type: "mask" }}
                  placement="topLeft"
                  popupClassName="cam-datepicker-popup"
                  style={{ width: "100%" }}
                  value={filterDateRange}
                  onCalendarChange={(dates) => {
                    datePickerSelecting.current = dates?.[0] ?? dates?.[1] ?? null;
                  }}
                  onChange={(dates) => {
                    setFilterDateRange(dates as [Dayjs, Dayjs] | null);
                    datePickerSelecting.current = null;
                  }}
                  disabledDate={(d) => {
                    if (d.isAfter(dayjs(), "day")) return true;
                    const anchor = datePickerSelecting.current;
                    if (!anchor) return false;
                    return d.isBefore(anchor.subtract(6, "month"), "day") || d.isAfter(anchor.add(6, "month"), "day");
                  }}
                />
              </div>
            )}
            <Button data-testid="cam-search-btn" type="primary" loading={isLoading} onClick={handleSearch} className="cam__joblist-filter-search">
              Search
            </Button>
          </div>

          {/* ── Desktop: table ── */}
          <div className="cam__joblist-table">
            <Table
              columns={columns}
              dataSource={jobs}
              rowKey="JobID"
              loading={isLoading}
              locale={{
                emptyText: isLoading
                  ? null
                  : hasSearched
                  ? "No jobs found for the selected filters."
                  : "Use the filters above to search for jobs.",
              }}
              expandable={{
                columnWidth: "3%",
                expandedRowRender: (record) => <RowButtons record={record} />,
              }}
              style={{ height: "100%" }}
              pagination={{ defaultPageSize: 200, showSizeChanger: false }}
              scroll={{ x: 1100, y: windowHeight - 240 }}
              onChange={handleChange}
            />
          </div>

          {/* ── Mobile: cards ── */}
          <div className="cam__joblist-cards">
            <Input.Search
              id="cam-card-search-input"
              placeholder="Search..."
              value={cardSearch}
              onChange={(e) => {
                setCardSearch(e.target.value);
                setCardPage(1);
              }}
              onSearch={(val) => {
                setCardSearch(val);
                setCardPage(1);
              }}
              allowClear
              style={{ marginBottom: 8 }}
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Sort by
                </Text>
                <Select
                  data-testid="cam-card-sort-by-select"
                  style={{ width: "100%" }}
                  value={cardSortBy}
                  options={CARD_SORT_OPTIONS}
                  onChange={(val) => { setCardSortBy(val); setCardPage(1); }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Direction
                </Text>
                <Select
                  data-testid="cam-card-sort-dir-select"
                  style={{ width: "100%" }}
                  value={cardSortDir}
                  options={[
                    { value: "asc", label: "Ascending" },
                    { value: "desc", label: "Descending" },
                  ]}
                  onChange={(val) => { setCardSortDir(val); setCardPage(1); }}
                />
              </div>
            </div>

            {sortedJobs.length === 0 ? (
              <Text type="secondary">
                {isLoading
                  ? "Loading..."
                  : !hasSearched
                  ? "Use the filters above to search for jobs."
                  : cardSearch
                  ? "No results found."
                  : "No jobs found for the selected filters."}
              </Text>
            ) : (
              <>
                {paginatedJobs.map((job) => (
                  <div key={job.JobID} className="cam__upload-card">
                    <div className="cam__upload-card-header">
                      <span className="cam__upload-card-filename">#{job.JobID}</span>
                    </div>
                    <div className="cam__upload-card-fields">
                      <div className="cam__upload-card-field">
                        <span className="cam__upload-card-label">Communication Type</span>
                        <span>{job.CommunicationType}</span>
                      </div>
                      <div className="cam__upload-card-field">
                        <span className="cam__upload-card-label">Property</span>
                        <span>{job.Property}</span>
                      </div>
                      <div className="cam__upload-card-field">
                        <span className="cam__upload-card-label">Processed By</span>
                        <span>{job.ProcessedBy}</span>
                      </div>
                      <div className="cam__upload-card-field">
                        <span className="cam__upload-card-label">Date Created</span>
                        <span>{job.DateCreated}</span>
                      </div>
                      <div className="cam__upload-card-field">
                        <span className="cam__upload-card-label">Status</span>
                        <span>{job.Status}</span>
                      </div>
                    </div>
                    <div className="cam__joblist-card-actions">
                      <RowButtons record={job} />
                    </div>
                  </div>
                ))}
                {paginatedJobs.length < sortedJobs.length && (
                  <Button
                    data-testid="cam-load-more-btn"
                    block
                    onClick={() => setCardPage((p) => p + 1)}
                    style={{ marginTop: 4 }}
                  >
                    Load more ({sortedJobs.length - paginatedJobs.length} remaining)
                  </Button>
                )}
              </>
            )}
          </div>

        </Spin>
      </Drawer>
    </>
  );
});

export default CommunicationType;